/**
 * RecallDetail (C-3) — single recall: metadata, lifecycle FSM actions,
 * reconciliation (qty recovered → effectiveness %), Related Records (C-4), timeline.
 * Route: /recalls/$id
 */
import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import styles from './Recalls.module.css';
import { RelatedRecords } from '../../components/RelatedRecords';
import {
  useRecallDetailQuery, useRecallTransition, useRecallReconcile,
  RECALL_FSM, STATUS_LABEL, CLASS_LABEL,
} from './useRecalls';

function fmtDT(iso: string | null | undefined) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return String(iso); }
}

export function RecallDetail({ recallId }: { recallId: string }) {
  const q = useRecallDetailQuery(recallId);
  const transition = useRecallTransition(recallId);
  const reconcile = useRecallReconcile(recallId);
  const [recovered, setRecovered] = useState('');
  const [msg, setMsg] = useState<string | null>(null);

  if (q.isLoading) return <main className={styles.page}><div className={styles.empty}>Loading recall…</div></main>;
  if (q.isError || !q.data) return <main className={styles.page}><div className={styles.empty}>Recall not found. <Link to="/recalls">Back to recalls</Link></div></main>;
  const r = q.data;
  const next = RECALL_FSM[r.status] ?? [];

  async function doTransition(to: string) {
    setMsg(null);
    try { await transition.mutateAsync({ toStatus: to }); }
    catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Transition failed.'); }
  }
  async function doReconcile() {
    setMsg(null);
    if (recovered === '' || Number.isNaN(Number(recovered))) { setMsg('Enter the quantity recovered.'); return; }
    try { await reconcile.mutateAsync({ qtyRecovered: Number(recovered) }); setRecovered(''); }
    catch (e: unknown) { setMsg(e instanceof Error ? e.message : 'Reconcile failed.'); }
  }

  return (
    <main className={styles.page}>
      <nav className={styles.crumbs} aria-label="Breadcrumb">
        <Link to="/recalls" className={styles.crumb}>Recalls</Link>
        <span className={styles.crumbSep} aria-hidden>/</span>
        <span>{r.recallNumber}</span>
      </nav>

      <header className={styles.detailHead}>
        <div>
          <h1 className={styles.detailTitle}><span className={styles.mono}>{r.recallNumber}</span> · {r.productName}</h1>
          <div className={styles.subline}>
            <span className={styles.statusPill}>{STATUS_LABEL[r.status] ?? r.status}</span>
            <span className={`${styles.pill} ${r.classification === 'CLASS_I' ? styles.cls1 : r.classification === 'CLASS_II' ? styles.cls2 : styles.cls3}`}>{CLASS_LABEL[r.classification] ?? r.classification}</span>
            {r.isMock && <span className={`${styles.pill} ${styles.mockPill}`}>Mock recall</span>}
          </div>
        </div>
        <div className={styles.actions}>
          {next.length === 0 ? <span className={styles.terminal}>Terminal state</span> : next.map((to) => (
            <button key={to} type="button" className={`${styles.btn} ${to === 'CANCELLED' ? styles.btnNeutral : styles.btnPrimary}`}
              onClick={() => void doTransition(to)} disabled={transition.isPending}>→ {STATUS_LABEL[to] ?? to}</button>
          ))}
        </div>
      </header>
      {msg && <div className={styles.err} role="alert">{msg}</div>}

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Recall detail</h2>
        <dl className={styles.metaGrid}>
          <div><dt>Batch(es)</dt><dd>{r.batchRefs ?? '—'}</dd></div>
          <div><dt>Markets</dt><dd>{r.markets ?? '—'}</dd></div>
          <div><dt>Qty released</dt><dd>{r.qtyReleased ?? '—'}</dd></div>
          <div><dt>Qty recovered</dt><dd>{r.qtyRecovered ?? '—'}</dd></div>
          <div><dt>Effectiveness</dt><dd>{r.effectivenessPct == null ? '—' : `${r.effectivenessPct}%`}</dd></div>
          <div><dt>Owner</dt><dd>{r.ownerName ?? '—'}</dd></div>
          <div><dt>Opened</dt><dd>{fmtDT(r.openedAt)}</dd></div>
          <div><dt>Closed</dt><dd>{fmtDT(r.closedAt)}</dd></div>
        </dl>
        <div className={styles.descBlock}><strong>Reason</strong><p>{r.reason}</p></div>
        {r.healthHazardEvaluation && <div className={styles.descBlock}><strong>Health-hazard evaluation</strong><p>{r.healthHazardEvaluation}</p></div>}
        {r.regulatoryNotification && <div className={styles.descBlock}><strong>Regulatory notification</strong><p>{r.regulatoryNotification}</p></div>}
      </section>

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>Reconciliation</h2>
        <div className={styles.reconRow}>
          <span className={styles.reconStat}><strong>{r.qtyRecovered ?? 0}</strong> / {r.qtyReleased ?? '—'} recovered</span>
          <span className={`${styles.reconPct} ${(r.effectivenessPct ?? 0) >= 90 ? styles.ocGood : (r.effectivenessPct ?? 0) >= 70 ? styles.ocWarn : styles.ocBad}`}>
            {r.effectivenessPct == null ? '—' : `${r.effectivenessPct}% effective`}
          </span>
        </div>
        <div className={styles.inlineForm}>
          <input type="number" value={recovered} onChange={(e) => setRecovered(e.target.value)} placeholder="Qty recovered" className={styles.textInput} />
          <button type="button" className={`${styles.btn} ${styles.btnPrimary}`} onClick={() => void doReconcile()} disabled={reconcile.isPending}>
            {reconcile.isPending ? 'Saving…' : 'Record recovery'}
          </button>
        </div>
      </section>

      {/* C-4 — Related Records (Complaint / Deviation / CAPA / OOS / Risk / CC). */}
      <RelatedRecords entityType="RECALL" entityId={r.recallId} />

      <section className={styles.panel}>
        <h2 className={styles.panelTitle}>History</h2>
        <ol className={styles.timeline}>
          {(r.history ?? []).map((h, i) => (
            <li key={i} className={styles.tlRow}>
              <span className={styles.tlWhen}>{fmtDT(h.at_utc)}</span>
              <span className={styles.tlWhat}>
                {h.event_kind === 'RECALL_TRANSITION' && h.from_status
                  ? `${STATUS_LABEL[h.from_status] ?? h.from_status} → ${STATUS_LABEL[h.to_status ?? ''] ?? h.to_status}`
                  : h.event_kind.replaceAll('_', ' ').toLowerCase()}
                {h.note ? <span className={styles.tlNote}> — {h.note}</span> : null}
              </span>
              {h.actor_name && <span className={styles.sub}>{h.actor_name}</span>}
            </li>
          ))}
        </ol>
      </section>
    </main>
  );
}
